'use client';

import { decodeString } from '@libs/common-next';

import {
    getSortUrlValueFromIndex,
    ORDERED_FILTERS,
    SORT_INDEX_PLP,
    SORT_INDEX_SEARCH,
    AlgoliaIndice,
} from '@libs/algolia';
import {
    useNumericMenu,
    usePagination,
    useRange,
    useRefinementList,
    useSearchBox,
    useSortBy,
} from 'react-instantsearch';

import type { UiState } from 'instantsearch.js';
import type QueryString from 'qs';
import type { Facets } from '@app/plp/types/facetedNavigation';
import type {
    IAlgoliaRouteState,
    IAlgoliaRefinementList,
    IAlgoliaUIState,
    IVirtualSortProps,
    IAlgoliaRefinement,
} from '@libs/algolia';
import type {
    UseNumericMenuProps,
    UseRangeProps,
    UseRefinementListProps,
} from 'react-instantsearch';

const PRICE_ATTRIBUTE = 'price.number';
export const CATEGORIES_ATTRIBUTE = 'custom_category_names';

export const getQueryParamsFromState = (
    routeState: UiState,
    isSearchPage: boolean
    // eslint-disable-next-line sonarjs/cognitive-complexity -- no
): IAlgoliaRouteState => {
    const queryParams: IAlgoliaRouteState = {};
    ORDERED_FILTERS.filter(
        e => !e.isNumeric && e.attribute !== 'brand'
    ).forEach((element: IAlgoliaRefinement) => {
        if (routeState[element.attribute]) {
            queryParams[element.urlName] = (
                routeState[element.attribute] as string[]
            ).map(encodeURIComponent);
        }
    });

    if (routeState.q) {
        queryParams.q = encodeURIComponent(routeState.q as string);
    }

    if (routeState.page && Number(routeState.page) !== 1) {
        queryParams.page = routeState.page.toString();
    }

    if (routeState.customBvAverageRating) {
        queryParams.rating = routeState.customBvAverageRating.toString();
    }

    if (routeState[PRICE_ATTRIBUTE]) {
        queryParams.price = routeState[PRICE_ATTRIBUTE].toString();
    }

    if (isSearchPage && routeState.sort) {
        queryParams.sort =
            getSortUrlValueFromIndex(routeState.sort as string) ?? '';
    }

    if (!isSearchPage && routeState.sort) {
        const sortRule =
            getSortUrlValueFromIndex(routeState.sort as string) ?? '';
        if (sortRule !== 'best-sellers') {
            queryParams.sort = sortRule;
        }
    }

    if (!isSearchPage && !routeState.sort) {
        queryParams.sort = 'relevance';
    }

    return queryParams;
};

export const setGeneralQuery = (
    refinements: IAlgoliaRefinementList,
    q?: string
) => {
    if (q) {
        refinements.query = decodeString(q as string);
    }

    return refinements;
};

export const getRefinementsFromQueryParams = (
    parsedqs: QueryString.ParsedQs,
    filterMap: Facets[]
): UiState => {
    let refinements: IAlgoliaRefinementList = {};
    refinements = Object.assign({}, ...(filterMap as Facets[]));
    return setGeneralQuery(refinements, parsedqs.q as string) as UiState;
};

export const getRouteFromState = (indexUiState: IAlgoliaUIState) => {
    const refinements: IAlgoliaRefinementList = {};
    if (indexUiState.refinementList) {
        ORDERED_FILTERS.filter(e => !e.isNumeric).forEach(
            (element: IAlgoliaRefinement) => {
                refinements[element.attribute] =
                    indexUiState.refinementList[element.attribute];
            }
        );
    }

    if (indexUiState.query) {
        refinements.q = indexUiState.query;
    }

    if (indexUiState.page) {
        refinements.page = indexUiState.page;
    }

    if (
        indexUiState.numericMenu &&
        indexUiState.numericMenu.customBvAverageRating
    ) {
        refinements.customBvAverageRating =
            indexUiState.numericMenu.customBvAverageRating;
    }

    if (
        indexUiState.range &&
        indexUiState.range[PRICE_ATTRIBUTE] &&
        indexUiState.range[PRICE_ATTRIBUTE] !== ':0'
    ) {
        refinements[PRICE_ATTRIBUTE] = indexUiState.range[PRICE_ATTRIBUTE];
    }

    if (indexUiState.sortBy) {
        refinements.sort = indexUiState.sortBy;
    }

    if (
        indexUiState.refinementList &&
        indexUiState.refinementList[CATEGORIES_ATTRIBUTE]
    ) {
        refinements[CATEGORIES_ATTRIBUTE] =
            indexUiState.refinementList[CATEGORIES_ATTRIBUTE];
    }

    return refinements as UiState;
};

export const getStateFromRoute = (
    routeState: IAlgoliaRouteState,
    isSearchPage: boolean
) => {
    const refinementList: IAlgoliaRefinementList = {};
    ORDERED_FILTERS.filter(e => !e.isNumeric).forEach(
        (element: IAlgoliaRefinement) => {
            refinementList[element.attribute] = routeState[
                element.urlName
            ] as string[];
        }
    );

    const numericMenu = {
        customBvAverageRating:
            routeState.customBvAverageRating &&
            !Array.isArray(routeState.customBvAverageRating)
                ? routeState.customBvAverageRating
                : null,
    };

    const priceRange = routeState[PRICE_ATTRIBUTE];

    const range = {
        [PRICE_ATTRIBUTE]:
            priceRange && !Array.isArray(priceRange) ? priceRange : null,
    };

    let { sort } = routeState;
    if (sort === '') {
        sort = isSearchPage
            ? AlgoliaIndice.PRODUCTS
            : AlgoliaIndice.PRODUCTS_BEST_SELLERS;
    }

    return {
        refinementList,
        page: routeState.page,
        sortBy: sort,
        numericMenu,
        range,
        query: routeState.query,
    } as UiState;
};

// virtual refinements for proper InstantSearchNext functionality
// eslint-disable-next-line func-style -- no
export function VirtualRefinementList(props: UseRefinementListProps) {
    useRefinementList(props);
    return null;
}
// eslint-disable-next-line func-style -- no
export function VirtualPagination() {
    usePagination();
    return null;
}
// eslint-disable-next-line func-style -- no
export function VirtualSortBy({ isSearchPage }: IVirtualSortProps) {
    useSortBy(isSearchPage ? SORT_INDEX_SEARCH : SORT_INDEX_PLP);
    return null;
}
// eslint-disable-next-line func-style -- no
export function VirtualNumericMenu(props: UseNumericMenuProps) {
    useNumericMenu(props);
    return null;
}
// eslint-disable-next-line func-style -- no
export function VirtualRangeInput(props: UseRangeProps) {
    useRange(props);
    return null;
}
// eslint-disable-next-line func-style -- no
export function VirtualSearchBox() {
    useSearchBox();
    return null;
}
